import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Grid2,
  IconButton,
  Typography,
  useTheme
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Datepicker, locale } from "@mobiscroll/react";
import { useTranslation } from "react-i18next";
import { DateTime, Interval } from "luxon";
import { useSnackbar } from "notistack";
import { useHistory, useParams } from "react-router";
import { useDispatch, useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { a11yProps } from "../../../../features/TabPanel/TabPanel.partial";
import { TabPanel } from "../../../../features/TabPanel/TabPanel.component";
import { BookingTimeTemp } from "../../../BookingForm/form-edit-time-dialog.component";
import {
  FormSelectionBookingType,
  ValidMaxAdvancedPerType
} from "../../../BookingForm/form-selection-bookingType.component";
import {
  BookingType,
  Frequence,
  NotSerializedBookingMode
} from "../../../../features/Booking-Form/typings/booking-inputs";
import useTimeFormat from "../../../../hooks/useTimeFormat/useTimeFormat";
import {
  clearFloors,
  clearInputs,
  setInputs
} from "../../../../features/Booking-Form/slices/booking.slice";
import {
  calculateMaxEndDate,
  getBookingType,
  updateInitialBookingTimeByNow
} from "../../../BookingForm/utils/booking.utils";
import { getDatesBetween } from "../functions/getDatesBetween.function";
import { useRemoteFetchCancelAutomatedBooking } from "../../../../hooks/Remote/CancelAutomatedBooking/useRemoteFetchCancelAutomatedBooking";
import { useRemoteUpdateCancelAutomatedBooking } from "../../../../hooks/Remote/CancelAutomatedBooking/useRemoteUpdateCancelAutomatedBooking";
import { setDatesAndValidate } from "../../../BookingForm/functions/date-time.functions";
import { handleValidMaxAdvancedPerBookingType } from "./ScheduleCreateOnCalendarDialog.functions";
import { CustomColoredTabs } from "../../../CustomColoredTabs/CustomColoredTabs.component";
import { CustomColoredTab } from "../../../CustomColoredTabs/CustomColoredTab.component";

interface Props {
  open: boolean;
  isCreateOnCalendar: {
    start: DateTime | null;
    end: DateTime | null;
    open: boolean;
  };
  onClose: () => void;
  hideAssistantTab?: boolean;
}

export function ScheduleCreateOnCalendarDialog({
  open,
  isCreateOnCalendar,
  onClose,
  hideAssistantTab = false
}: Readonly<Props>) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const {
    userInformation: {
      sub,
      firstName,
      surname,
      email,
      company: { meta }
    },
    settings
  } = useSelector((state: RootState) => state.login);
  const { inputs } = useSelector((state: RootState) => state.booking);
  const dispatch = useDispatch();

  const { step }: { step: string } = useParams();
  const history = useHistory();
  const { timeFormat } = useTimeFormat();

  const [bookingTime, setBookingTime] = useState<BookingTimeTemp>({
    bookingStart: settings.standardStartTime,
    bookingEnd: settings.standardEndTime
  });
  const [bookStep, setBookStep] = useState<"time" | "type">("time");
  const [isCancelAutomated, setIsCancelAutomated] = useState(false);
  const [isCancelAutomatedUpdating, setIsCancelAutomatedUpdating] = useState(false);
  const [tabState, setTabState] = useState(0);
  const [message, setMessage] = useState<string>();
  const [validDates, setValidDates] = useState(false);
  const [validMaxAdvancedPerType, setValidMaxAdvancedPerType] = useState<ValidMaxAdvancedPerType>({
    workplace: false,
    parkingplace: false,
    electriccharging: false,
    conference: false,
    vehicle: false,
    visitor: false
  });
  const showAssistantTab =
    !hideAssistantTab && meta.workplaceBookingEnabled && meta.bookingAssistenceActive;

  const { data: cancelAutomatedBookingData, refetch: refetchCancelAutomatedBookingData } =
    useRemoteFetchCancelAutomatedBooking({
      startDate: isCreateOnCalendar.start?.toFormat("yyyy-MM-dd"),
      endDate: isCreateOnCalendar.start?.toFormat("yyyy-MM-dd")
    });
  const { mutateAsync: mutateCancelAutomatedBooking } = useRemoteUpdateCancelAutomatedBooking();

  const handleTabChange = (event: any, newValue: number) => setTabState(newValue);
  const onSelectBookingMode = (mode: NotSerializedBookingMode) => onModeChange(mode);

  const handleNextBookStep = () => {
    if (bookStep === "time") {
      dispatch(
        setInputs({ bookingStart: bookingTime.bookingStart, bookingEnd: bookingTime.bookingEnd })
      );
      setBookStep("type");
    } else setBookStep("time");
  };

  const onModeChange = (mode: NotSerializedBookingMode) => {
    if (!isCreateOnCalendar.start || !isCreateOnCalendar.end) return;

    const { start: startDate, end: endDate } = isCreateOnCalendar;
    const dateInterval = Interval.fromDateTimes(startDate, endDate).length("day") > 1;
    const specificDays = getDatesBetween(startDate, endDate).map(date =>
      date.toFormat("yyyy-MM-dd")
    );
    const isVehicleMultiDayRange = mode === BookingType.VEHICLE && dateInterval;
    const frequenceByInterval = dateInterval ? Frequence.SPECIFIC : Frequence.SINGLE;
    const frequence = isVehicleMultiDayRange ? Frequence.SINGLE : frequenceByInterval;

    dispatch(clearInputs());
    dispatch(clearFloors());

    dispatch(
      setInputs({
        mode: mode,
        bookingType: getBookingType(mode),
        usersBookedFor: [
          {
            userId: sub,
            firstName,
            surname: surname,
            isExternal: false,
            email: email
          }
        ],
        bookingStart: inputs.bookingStart,
        bookingEnd: inputs.bookingEnd,
        bookingFrom: startDate.toFormat("yyyy-MM-dd"),
        bookingTo: endDate.toFormat("yyyy-MM-dd"),
        frequence,
        specificDays: isVehicleMultiDayRange ? undefined : specificDays,
        isUserBookedBy: true,
        automatedSeating: false
      })
    );

    history.push("/booking/" + 2, { prev: Number.parseInt(step) });
  };

  function handleChangeCancelAutomated(event: any, checked: boolean) {
    // handle change the checkbox to cancel automated
    setIsCancelAutomated(checked);
  }

  function confirmCancelAutomatedBooking() {
    let addedDates: string[] = [];
    let deletedDates: string[] = [];

    if (!isCreateOnCalendar.start || !isCreateOnCalendar.end) return;

    // get dates between as DateTime
    const datesBetween = getDatesBetween(isCreateOnCalendar.start, isCreateOnCalendar.end);
    const dates = datesBetween.map(date => date.toFormat("yyyy-MM-dd"));

    if (isCancelAutomated) addedDates = [...dates];
    if (!isCancelAutomated) deletedDates = [...dates];

    setIsCancelAutomatedUpdating(true);
    mutateCancelAutomatedBooking({ addedDates, deletedDates })
      .then(() => {
        const successDesc =
          addedDates.length > 0
            ? "_updateAddedCancellationAutomatedBookingAssistance"
            : "_updateDeletedCancellationAutomatedBookingAssistance";

        enqueueSnackbar(t(successDesc), { variant: "success" });
      })
      .catch(() =>
        enqueueSnackbar(t("_errorUpdateCancellationAutomatedBookingAssistance"), {
          variant: "error"
        })
      )
      .finally(() => {
        setIsCancelAutomatedUpdating(false);
        refetchCancelAutomatedBookingData();
        onClose();
      });
  }

  // initially set whether isCancelAutomatedBooking or not
  useEffect(() => {
    if (!isCreateOnCalendar.start || !isCreateOnCalendar.end) return;
    const isMultipleDatesSelected =
      isCreateOnCalendar.end.diff(isCreateOnCalendar.start).valueOf() > 0;

    if (isMultipleDatesSelected) setIsCancelAutomated(false);

    // check whether the selected date has initial value
    if (cancelAutomatedBookingData && cancelAutomatedBookingData.length > 0) {
      setIsCancelAutomated(true);
    }
  }, [cancelAutomatedBookingData]);

  useEffect(() => {
    if (!open || !isCreateOnCalendar.start || !isCreateOnCalendar.end) return;

    const { start, end } = isCreateOnCalendar;
    const bookingFrom = start.toFormat("yyyy-MM-dd");
    const bookingTo = end.toFormat("yyyy-MM-dd");
    const dateInterval = Interval.fromDateTimes(start, end).length("day") > 1;
    const specificDays = getDatesBetween(start, end).map(d => d.toFormat("yyyy-MM-dd"));
    const isVehicleMultiDayRange = inputs.mode === BookingType.VEHICLE && dateInterval;
    const frequenceByInterval = dateInterval ? Frequence.SPECIFIC : Frequence.SINGLE;
    const frequence = isVehicleMultiDayRange ? Frequence.SINGLE : frequenceByInterval;
    const bookingSpecificDays = isVehicleMultiDayRange ? undefined : specificDays;
    const weekdaysAllTrue = {
      Monday: true,
      Tuesday: true,
      Wednesday: true,
      Thursday: true,
      Friday: true,
      Saturday: true,
      Sunday: true
    };

    dispatch(
      setInputs({
        bookingFrom,
        bookingTo,
        frequence,
        specificDays: bookingSpecificDays,
        ...(dateInterval ? { weekdays: weekdaysAllTrue } : {})
      })
    );

    const maxDate = calculateMaxEndDate({ metaData: meta, bookingMode: inputs.mode });
    updateInitialBookingTimeByNow(
      {
        ...inputs,
        bookingFrom,
        bookingTo,
        frequence,
        specificDays: bookingSpecificDays,
        weekdays: weekdaysAllTrue
      },
      settings,
      maxDate,
      dispatch
    );
  }, [open, isCreateOnCalendar.start, isCreateOnCalendar.end]);

  useEffect(() => {
    // initial booking time based on selected date
    setBookingTime({
      bookingStart: inputs.bookingStart as string,
      bookingEnd: inputs.bookingEnd as string
    });
  }, [inputs.bookingStart, inputs.bookingEnd]);

  useEffect(() => {
    if (!bookingTime.bookingStart || !bookingTime.bookingEnd) return;

    const maxDate = calculateMaxEndDate({ metaData: meta, bookingMode: inputs.mode });
    setDatesAndValidate(
      {
        ...inputs,
        bookingStart: bookingTime.bookingStart,
        bookingEnd: bookingTime.bookingEnd
      },
      setMessage,
      setValidDates,
      maxDate
    );
  }, [inputs, setValidDates, bookingTime, t, meta]);

  useEffect(() => {
    const { end } = isCreateOnCalendar;
    const endTime = bookingTime.bookingEnd ?? settings.standardEndTime ?? "23:59";

    if (!end) return;

    handleValidMaxAdvancedPerBookingType({
      end,
      endTime,
      metaData: meta,
      setValidMaxAdvancedPerType
    });
  }, [bookStep, bookingTime.bookingEnd]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { backgroundImage: "none", width: "400px" } } }}
    >
      <DialogContent>
        <Grid2 sx={{ float: "right", mt: "5px" }}>
          <IconButton onClick={onClose} data-testid="close-scheduleCreateOnCalendarDialog">
            <Close />
          </IconButton>
        </Grid2>

        <CustomColoredTabs value={tabState} variant="fullWidth" onChange={handleTabChange}>
          <CustomColoredTab index={0} data-testid={"tab1"} label={t("Booking")} {...a11yProps(0)} />
          {showAssistantTab && (
            <CustomColoredTab
              index={1}
              data-testid={"tab2"}
              label={t("Assistant")}
              {...a11yProps(1)}
            />
          )}
        </CustomColoredTabs>

        {/* tab - create new booking schedule */}
        <TabPanel value={tabState} index={0}>
          {bookStep === "time" && (
            <Grid2 container sx={{ flexDirection: "column" }}>
              <Grid2 sx={{ alignSelf: "center" }}>
                <Datepicker
                  select="range"
                  onChange={picker => {
                    if (picker.value && Array.isArray(picker.value)) {
                      if (picker.value[0]) {
                        const bs = DateTime.fromJSDate(new Date(picker.value[0])).toFormat("HH:mm");
                        setBookingTime((prevState: BookingTimeTemp) => ({
                          ...prevState,
                          bookingStart: bs
                        }));
                      }
                      if (picker.value[1]) {
                        const be = DateTime.fromJSDate(new Date(picker.value[1])).toFormat("HH:mm");
                        setBookingTime((prevState: BookingTimeTemp) => ({
                          ...prevState,
                          bookingEnd: be
                        }));
                      }
                    }
                  }}
                  data-testid={"timePicker"}
                  controls={["time"]}
                  timeFormat={timeFormat}
                  locale={locale[i18n.language]}
                  value={[bookingTime.bookingStart, bookingTime.bookingEnd]}
                  stepMinute={15}
                  inputProps={{ style: { textAlign: "center", width: "fit-content" } }}
                />
              </Grid2>
            </Grid2>
          )}

          {/* booking type selection part */}
          {bookStep !== "time" && (
            <Grid2
              container
              data-testid="createOnCalendar-selction-bookingType-container"
              sx={{ justifyContent: "center", textAlign: "center" }}
            >
              <FormSelectionBookingType
                onModeChange={onSelectBookingMode}
                iconSize={{ width: "80px", height: "60px" }}
                padding={2}
                margin={1}
                maxWidth={"140px"}
                showTitle={false}
                validMaxAdvancedPerType={validMaxAdvancedPerType}
              />
            </Grid2>
          )}

          {/* book step next */}
          <Grid2
            sx={{
              display: "flex",
              justifyContent: "center",
              columnGap: 2,
              mt: 3,
              flexDirection: "column",
              alignItems: "center"
            }}
          >
            {/* book time warning */}
            {!validDates && (
              <Typography
                sx={{ textAlign: "left", color: theme.palette.error.main, position: "relative" }}
                variant={"body2"}
              >
                {message}
              </Typography>
            )}

            {/* book step next button */}
            <Button
              data-testid="book-next-step-btn"
              sx={{ width: "80px", mt: 2 }}
              color={bookStep === "time" ? "primary" : "secondary"}
              onClick={handleNextBookStep}
              disabled={!validDates}
            >
              {t(bookStep === "time" ? "Next" : "Back")}
            </Button>
          </Grid2>
        </TabPanel>

        {/* tab - assistant */}
        {showAssistantTab && (
          <TabPanel value={tabState} index={1}>
            <FormGroup sx={{ ml: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="cancel-automated-booking-checkBox"
                    checked={!!isCancelAutomated}
                    onChange={handleChangeCancelAutomated}
                  />
                }
                label={t("Ignore this time period for automated booking")}
              />

              <Grid2
                container
                data-testid="createOnCalendar-cancel-automatedBook-container"
                sx={{ justifyContent: "center", textAlign: "center" }}
              >
                <Button
                  loading={isCancelAutomatedUpdating}
                  data-testid="confirm-cancel-automatedBooking-btn"
                  sx={{ mt: 2 }}
                  onClick={confirmCancelAutomatedBooking}
                  variant={"contained"}
                >
                  {t("Confirm")}
                </Button>
              </Grid2>
            </FormGroup>
          </TabPanel>
        )}
      </DialogContent>
    </Dialog>
  );
}
